// Centralized API Paths Configuration
// All API endpoint paths used throughout the application

export const API_PATHS = {
  // Authentication
  ADMIN_LOGIN_INITIATE: '/adminLoginInitiate',
  ADMIN_LOGIN_VERIFY_OTP: '/adminLoginVerifyOtp',
  ADMIN_LOGOUT: '/adminLogout',
  ADMIN_FORGOT_PASSWORD_INITIATE: '/adminForgotPasswordInitiate',
  ADMIN_FORGOT_PASSWORD_VERIFY_OTP: '/adminForgotPasswordVerifyOtp',
  ADMIN_RESET_PASSWORD: '/adminResetPassword',
  MERCHANT_LOGIN_INITIATE: '/merchantLoginInitiate',
  MERCHANT_LOGIN_VERIFY_OTP: '/merchantLoginVerifyOtp',
  MERCHANT_FORGOT_PASSWORD_INITIATE: '/merchantForgotPasswordInitiate',
  MERCHANT_FORGOT_PASSWORD_VERIFY_OTP: '/merchantForgotPasswordVerifyOtp',
  MERCHANT_RESET_PASSWORD: '/merchantResetPassword',
  // Unified, role-agnostic flow — backend detects admin vs merchant from the email/token, so the
  // frontend never needs to ask or know which. Prefer these over the role-specific ones above.
  FORGOT_PASSWORD_INITIATE: '/forgotPasswordInitiate',
  FORGOT_PASSWORD_VERIFY_OTP: '/forgotPasswordVerifyOtp',
  RESET_PASSWORD: '/resetPassword',
  MERCHANT_SIGNUP: '/merchantSignUp',
  IMPERSONATE_USER: '/impersonateUser',

  // Merchants
  ADD_MERCHANT: '/addMerchant',
  UPDATE_MERCHANT: '/updateMerchant',
  GET_MERCHANTS: '/getMerchants',
  GET_MERCHANT: '/merchants', // Used with ID: /merchants/{id}
  GET_MERCHANT_TERMINALS: '/getMerchantTerminals',
  GET_ALL_MERCHANT_TERMINALS: '/getAllMerchantTerminals',
  GET_MERCHANT_TERMINAL_BY_ID: '/getMerchantTerminalById',

  // Acquirers
  GET_ACQUIRERS: '/getAcquirers',
  GET_ACQUIRERS_BY_STATUS: '/getAcquirersByStatus',
  GET_MERCHANT_ACQUIRER_MAPPINGS: '/getMerchantAcquirerMappings',
  ADD_ACQUIRER: '/addAcquirer',
  UPDATE_ACQUIRER: '/updateAcquirer',
  GET_ACQUIRER: '/acquirers', // Used with ID: /acquirers/{id}
  GET_ACQUIRER_TERMINALS: '/getAcquirerTerminals',
  GET_ALL_ACQUIRER_TERMINALS: '/getAllAcquirerTerminals',
  GET_ACQUIRER_TERMINAL_BY_ID: '/getAcquirerTerminalById',

  // MIDs (Merchant Identification Numbers)
  ADD_MID: '/mids',
  GET_MIDS: '/getMids', // If exists

  // Terminals
  ADD_ACQUIRER_TERMINAL: '/addAcquirerTerminal',
  UPDATE_ACQUIRER_TERMINAL: '/updateAcquirerTerminal',
  ADD_MERCHANT_TERMINAL: '/addMerchantTerminal',
  UPDATE_MERCHANT_TERMINAL: '/updateMerchantTerminal',
  ADD_TERMINAL_MAPPING: '/addTerminalMapping',
  UPDATE_TERMINAL_MAPPING: '/updateTerminalMapping',
  GET_APM_SCHEMES: '/getApmSchemes',

  // Trancation Monitoring
  CREATE_ORDER: '/processPayment',
  GET_ORDERS:'/getOrders',
  EXPORT_ORDERS: '/exportOrders',
  GET_ORDER_ANALYTICS: '/getOrderAnalytics',
  GET_TRANSACTIONS:'/getTransactions',
  GET_OVERVIEW: '/getOverview',
  ADD_ORDER: '/addOrder',
  EXPORT: '/export',

  // Hosted checkout
  CHECKOUT_CREATE_ORDER: '/checkout/orders',
  PAYMENT_REDIRECT_STATUS: '/payments/redirect/status',

  // Reference Data
  GET_ACTIVE_COUNTRIES: '/getActiveCountries',
  GET_ACTIVE_FLOWS: '/getActiveFlows',
  GET_ACTIVE_INDUSTRIES: '/getActiveIndustries',
  GET_ACTIVE_TRAFFIC: '/getActiveTraffic',
  GET_ACTIVE_PAYMENT_METHODS: '/getActivePaymentMethods',
  GET_ACTIVE_CATEGORIES: '/getActiveCategories',
  GET_MERCHANTS_BY_STATUS: '/getMerchantsByStatus',
  GET_ROLES: '/getRoles',
  GET_PLATFORM_USERS: '/getPlatformUsers',
  UPDATE_PLATFORM_USER: '/updatePlatformUser',
  GET_USER_RECENT_LOGINS: '/getUserRecentLogins',
  GET_USER_PERMISSIONS: '/getUserPermissions',
  SAVE_USER_PERMISSIONS: '/saveUserPermissions',
  PROVISION_USER: '/provisionUser',

  // DCC (Dynamic Currency Conversion) markup rates — admin-only
  ADD_DCC_MARKUP_RATE: '/dccMarkupRates',
  GET_DCC_MARKUP_RATES_BY_TERMINAL: '/merchantTerminals', // used with /{terminalId}/dccMarkupRates

  // Storefront URL whitelisting — merchants submit URLs for admin approval
  // Storefront URL Submissions — SUBMIT_STOREFRONT_URL/GET_MY_STOREFRONT_URLS are merchant-facing;
  // GET_ALL_STOREFRONT_URLS is admin-only (separate path, not just a different verb on the same one)
  SUBMIT_STOREFRONT_URL: '/storefrontUrls',
  GET_MY_STOREFRONT_URLS: '/storefrontUrls/mine',
  GET_ALL_STOREFRONT_URLS: '/storefrontUrls/all',
  STOREFRONT_URL_TEMPLATE: '/storefrontUrls/template',
  BULK_UPLOAD_STOREFRONT_URLS: '/storefrontUrls/bulk-upload',

  // Audit / activity logs — admin-only paginated list (POST /list with { page, size }).
  GET_AUDIT_LOGS: '/list',

  // Payment Routing Engine — merchant-facing. A merchant session is pinned server-side to its own
  // merchant; an admin passes ?merchantId=. See RoutingRuleServiceImpl.resolveMerchantScopeId.
  GET_ROUTING_OVERVIEW: '/routing/overview',
  GET_ROUTING_RULES: '/routing/rules',
  CREATE_ROUTING_RULE: '/routing/rules',
  REORDER_ROUTING_RULES: '/routing/rules/reorder',
  GET_ROUTING_MIDS: '/routing/mids',
  ROUTING_FALLBACK_MESSAGE: '/routing/fallback-message',
  SIMULATE_ROUTING: '/routing/simulate',
  GET_ROUTING_RISK_FIELDS: '/routing/risk-fields',
  GET_ROUTING_MERCHANT_TERMINALS: '/routing/merchant-terminals',
  GET_ERROR_MAPPING_TERMINALS: '/error-mappings/terminals',
  GET_ERROR_CATALOG: '/error-mappings/catalog',
  GET_ERROR_MAPPINGS: '/error-mappings',
  CREATE_ERROR_MAPPING: '/error-mappings',
  // Risk screening (PaymentRiskGate)
  GET_FRAUD_DECISIONS: '/fraud/decisions',
  GET_FRAUD_SUMMARY: '/fraud/summary',
  GET_FRAUD_BLOCKLIST: '/fraud/blocklist',
  ADD_FRAUD_BLOCKLIST_ENTRY: '/fraud/blocklist',
  // Global shadow/enforce toggle (GET reads, PUT sets). Persisted server-side, not a property.
  FRAUD_MODE: '/fraud/mode',
} as const;

export function getDeleteFraudBlocklistPath(id: number): string {
  return `/fraud/blocklist/${id}`;
}

// Per-acquirer risk rules — GET reads the acquirer's rule set (merged with every known rule type),
// PUT upserts it. Same path, different verb, matching AcquirerRiskRulesController.
export function getAcquirerRiskConfigPath(acquirerId: string | number): string {
  return `/fraud/rules/acquirer/${acquirerId}`;
}

export const getUpdateErrorMappingPath = (id: string | number): string =>
  `${API_PATHS.GET_ERROR_MAPPINGS}/${id}`;
export const getToggleErrorMappingPath = (id: string | number, active: boolean): string =>
  `${API_PATHS.GET_ERROR_MAPPINGS}/${id}/active?active=${active}`;
export const getDeleteErrorMappingPath = (id: string | number): string =>
  `${API_PATHS.GET_ERROR_MAPPINGS}/${id}/delete`;

// Type for API path keys
export type ApiPathKey = keyof typeof API_PATHS;

// Helper function to get full API path
export const getApiPath = (key: ApiPathKey): string => {
  return API_PATHS[key];
};

// Helper function to get API path with ID
export const getApiPathWithId = (key: ApiPathKey, id: string | number): string => {
  return `${API_PATHS[key]}/${id}`;
};

export const getPaymentRedirectStatusPagePath = (transactionId: string): string => {
  return `${API_PATHS.PAYMENT_REDIRECT_STATUS}/${encodeURIComponent(transactionId)}`;
};

export const getAcquirerSoftDeletePath = (id: string | number): string => {
  return `${API_PATHS.GET_ACQUIRER}/${id}/softDelete`;
};

export const getMerchantSoftDeletePath = (merchantId: string | number): string => {
  return `${API_PATHS.GET_MERCHANT}/${merchantId}/softDelete`;
};

export const getAcquirerStatusPath = (id: string | number): string => {
  return `${API_PATHS.GET_ACQUIRER}/${id}/status`;
};

export const getMerchantStatusPath = (merchantId: string | number): string => {
  return `${API_PATHS.GET_MERCHANT}/${merchantId}/status`;
};

export const getMerchantPriorityRoutingPath = (merchantId: string | number): string => {
  return `${API_PATHS.GET_MERCHANT}/${merchantId}/priority-routing`;
};

export const getMerchantWebhookUrlPath = (merchantId: string | number): string => {
  return `${API_PATHS.GET_MERCHANT}/${merchantId}/webhook-url`;
};

export const getMerchantTerminalSoftDeletePath = (terminalId: string | number): string => {
  return `/merchantTerminals/${terminalId}/softDelete`;
};

export const getAcquirerTerminalSoftDeletePath = (terminalId: string | number): string => {
  return `/acquirerTerminals/${terminalId}/softDelete`;
};

export const getTerminalMappingSoftDeletePath = (id: string | number): string => {
  return `/terminalMappings/${id}/softDelete`;
};
export const getDccMarkupRatesForTerminalPath = (terminalId: string | number): string => {
  return `${API_PATHS.GET_DCC_MARKUP_RATES_BY_TERMINAL}/${terminalId}/dccMarkupRates`;
};

export const getDeactivateDccMarkupRatePath = (rateId: string | number): string => {
  return `${API_PATHS.ADD_DCC_MARKUP_RATE}/${rateId}/deactivate`;
};

export const getTransactionDccDetailPath = (transactionId: string): string => {
  return `/transactions/${encodeURIComponent(transactionId)}/dccDetail`;
};

export const getMarkTransactionRefundPath = (transactionId: string): string => {
  return `/transactions/${encodeURIComponent(transactionId)}/markRefund`;
};

export const getMarkTransactionChargebackPath = (transactionId: string): string => {
  return `/transactions/${encodeURIComponent(transactionId)}/markChargeback`;
};

export const GET_TRANSACTION_DCC_DETAILS_BATCH = '/transactions/dccDetails/batch';

export const getTerminalDccStatusPath = (terminalId: string | number): string => {
  return `${API_PATHS.GET_DCC_MARKUP_RATES_BY_TERMINAL}/${terminalId}/dccStatus`;
};

export const getApproveStorefrontUrlPath = (id: string | number): string => {
  return `${API_PATHS.SUBMIT_STOREFRONT_URL}/${id}/approve`;
};

export const getRejectStorefrontUrlPath = (id: string | number): string => {
  return `${API_PATHS.SUBMIT_STOREFRONT_URL}/${id}/reject`;
};
export const getUpdateRoutingRulePath = (ruleId: string | number): string => {
  return `${API_PATHS.GET_ROUTING_RULES}/${ruleId}`;
};

export const getToggleRoutingRulePath = (ruleId: string | number, active: boolean): string => {
  return `${API_PATHS.GET_ROUTING_RULES}/${ruleId}/active?active=${active}`;
};


// POST, not DELETE — the backend deliberately uses a distinct path rather than a distinct verb,
// since RoleAuthorizationFilter matches on path only (see RoutingRuleController.deleteRule).
export const getDeleteRoutingRulePath = (ruleId: string | number): string => {
  return `${API_PATHS.GET_ROUTING_RULES}/${ruleId}/delete`;
};
